import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@/store';
import { toggleThemeMode, setLanguage, setGlobalSearchOpen } from '@/store/slices/uiSlice';
import { useAuth } from '@/hooks/useAuth';
import { useGetMeQuery } from '@/store/api/usersApi';
import { useNotifications } from '@/hooks/useNotifications';
import { AiChatEntry } from '@/components/ai/AiChatEntry';
import { notificationPath } from './notificationTarget';
import { formatDateTime } from '@/utils/format';

/** Was die Glocke von einer Benachrichtigung liest — der Dienst liefert mehr. */
interface BellNotification {
  id: string;
  subject: string;
  isRead: boolean;
  createdAt: string;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
}

interface TopAppBarProps {
  onMenuToggle: () => void;
}

export const TopAppBar: React.FC<TopAppBarProps> = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const themeMode = useAppSelector((state) => state.ui.themeMode);
  const { user, logout } = useAuth();
  // Der Name aus dem eigenen Profil: Das Zugangstoken trägt keinen, und hier
  // stand bis 0.3.0 für jede Person „Admin“ (Testgrundlage 2.2, B-02).
  const { data: profil } = useGetMeQuery(undefined, { skip: !user });
  const vorname = profil?.firstName || user?.firstName || '';
  const nachname = profil?.lastName || user?.lastName || '';
  const anzeigename = [vorname, nachname].filter(Boolean).join(' ') || user?.email || '';
  const kuerzel = vorname ? `${vorname[0]}${nachname[0] ?? ''}` : user?.email?.[0]?.toUpperCase() || '?';
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const bellItems = notifications as unknown as BellNotification[];

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notifMenuAnchor, setNotifMenuAnchor] = useState<null | HTMLElement>(null);

  const handleLanguageToggle = () => {
    const newLang = i18n.language === 'de' ? 'en' : 'de';
    i18n.changeLanguage(newLang);
    dispatch(setLanguage(newLang));
  };

  /**
   * Eine Benachrichtigung öffnen: als gelesen markieren und zum Datensatz
   * gehen. Scheitert das Markieren, geht es trotzdem weiter — die Person will
   * den Datensatz sehen, nicht eine Fehlermeldung über einen Zähler.
   */
  const handleOpenNotification = async (notification: BellNotification) => {
    setNotifMenuAnchor(null);
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id);
      } catch {
        // Der Zähler bleibt dann stehen; beim nächsten Abruf stimmt er wieder.
      }
    }
    const path = notificationPath(notification.relatedEntityType, notification.relatedEntityId);
    if (path) navigate(path);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
    } catch {
      // Siehe oben — kein Grund, die Liste zu blockieren.
    }
  };

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    await logout();
  };

  return (
    <MuiAppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: '#1e3a5f',
        color: '#ffffff',
      }}
    >
      <Toolbar sx={{ minHeight: 56 }}>
        <IconButton edge="start" sx={{ mr: 2, color: '#ffffff' }} onClick={onMenuToggle}>
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.25rem',
            letterSpacing: '-0.01em',
            color: '#ffffff',
          }}
        >
          {t('app.title')}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* Der Co-Auditor — zweiter Einstieg neben dem schwebenden Knopf. */}
        <AiChatEntry />

        {/* Search */}
        <Tooltip title={t('actions.search')}>
          <IconButton color="inherit" onClick={() => dispatch(setGlobalSearchOpen(true))}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        {/* Language Toggle */}
        <Tooltip title={t('common.language')}>
          <Button
            size="small"
            onClick={handleLanguageToggle}
            startIcon={<LanguageIcon />}
            sx={{ mx: 1, minWidth: 'auto', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)' }}
          >
            {i18n.language}
          </Button>
        </Tooltip>

        {/* Theme Toggle */}
        <Tooltip title={themeMode === 'light' ? t('common.darkMode') : t('common.lightMode')}>
          <IconButton color="inherit" onClick={() => dispatch(toggleThemeMode())}>
            {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>

        {/* Notifications */}
        <Tooltip title={t('notifications.title')}>
          <IconButton color="inherit" onClick={(e) => setNotifMenuAnchor(e.currentTarget)}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User Menu */}
        <Tooltip title={anzeigename}>
          <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ ml: 1 }}>
            <Avatar
              sx={{ width: 32, height: 32, bgcolor: '#3b6cb5', fontSize: '0.875rem' }}
              src={user?.avatarUrl}
            >
              {kuerzel}
            </Avatar>
          </IconButton>
        </Tooltip>

        {/* User Dropdown */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" data-testid="angemeldete-person">{anzeigename}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
          </Box>
          <Divider />
          {/* Beide Punkte schlossen bisher nur das Menü. */}
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              navigate('/profile');
            }}
          >
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('common.profile')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              navigate('/profile/settings');
            }}
          >
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('common.settings')}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('auth.logout')}</ListItemText>
          </MenuItem>
        </Menu>

        {/* Notifications Dropdown */}
        <Menu
          anchorEl={notifMenuAnchor}
          open={Boolean(notifMenuAnchor)}
          onClose={() => setNotifMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('notifications.title')}</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllRead}>
                {t('notifications.markAllRead')}
              </Button>
            )}
          </Box>
          <Divider />
          {/* Hier stand fest „Keine neuen Benachrichtigungen", gleich was der
              Zähler sagte — die Liste war nie angebunden. */}
          {bellItems.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('notifications.noNotifications')}
              </Typography>
            </Box>
          ) : (
            bellItems.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleOpenNotification(notification)}
                sx={{
                  whiteSpace: 'normal',
                  alignItems: 'flex-start',
                  borderLeft: 3,
                  borderLeftColor: notification.isRead ? 'transparent' : 'primary.main',
                }}
              >
                <ListItemText
                  primary={notification.subject}
                  secondary={formatDateTime(notification.createdAt)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: notification.isRead ? 400 : 600,
                  }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            ))
          )}
        </Menu>
      </Toolbar>
    </MuiAppBar>
  );
};
